import React from 'react';
import { AIBadge } from '../../AiBadge';
import { motion } from 'framer-motion';
import { Alert } from 'antd';
import { HomeCard } from '../HomeCard';
import { VideoWrapper } from './VideoWrapper';
import FileTextIcon from '../../../icons/FileTextIcon';
import CheckIcon from '../../../icons/CheckIcon';
import SparkleIcon from '../../../icons/SparkleIcon';
import Utils from '../../../../utils/Utils';
import TrendingIcon from '../../../icons/TrendingIcon';
import FileTextRoundIcon from '../../../icons/FileTextRoundIcon';
import TrendingRoundIcon from '../../../icons/TrendingRoundIcon';
const improvementItems = [
  {
    type: 'success',
    title: 'Add quantifiable achievements',
    description: 'Show impact with numbers and metrics',
    icon: 'https://c.animaapp.com/mibc0aags5LdA5/img/check-icon.svg',
    bgColor: 'bg-success-50',
    borderColor: 'border-[#a6f4c5]',
  },
  {
    type: 'warning',
    title: 'Highlight leadership experience',
    description: 'Emphasize team management skills',
    icon: 'https://c.animaapp.com/mibc0aags5LdA5/img/check-icon-1.svg',
    bgColor: 'bg-warning-50',
    borderColor: 'border-[#fede88]',
  },
];

const skillItems = [
  {
    name: 'React',
    percentage: 70,
    tooltipOffset: 'mr-[-133.4px]',
    barOffset: 'mr-[-116.4px]',
  },
  {
    name: 'TypeScript',
    percentage: 40,
    tooltipOffset: 'mr-[98.6px]',
    barOffset: 'mr-[120.6px]',
  },
  {
    name: 'Docker',
    percentage: 60,
    tooltipOffset: 'mr-[-21.4px]',
    barOffset: '',
  },
];

export const UserDashboardSection = (): JSX.Element => {
  return (
    <section className="flex flex-col w-full items-center justify-center gap-1 py-20 bg-white">
      <div className="flex flex-col w-full items-start gap-10">
        <div className="flex flex-col w-full items-start gap-8 px-8 py-0">
          <motion.div
            className="flex flex-col items-center gap-12 w-full"
            initial={{ scale: 1.5 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1.18, ease: 'easeOut', type: 'tween' }}>
            <div className="flex flex-col max-w-[768px] items-center gap-3">
              <div className="flex flex-col items-center gap-3 w-full">
                <div className="inline-flex items-start">
                  <div className="inline-flex items-center gap-3 pl-1 pr-2.5 py-1 rounded-2xl">
                    <div className="inline-flex items-center">
                      <AIBadge title="AI Career Assistance" />
                    </div>
                  </div>
                </div>

                <h2 className="m-0 w-full font-sans font-semibold text-[36px] leading-[44px] tracking-[-0.02em] text-center text-gray-900">
                  <span>Your personal&nbsp;</span>
                  <span className="text-primary">Career</span>
                  <span>&nbsp;companion</span>
                </h2>
              </div>

              <p className="m-0 max-w-[718px] font-sans text-[20px] leading-[30px] tracking-normal font-light text-center text-gray-600">
                Get personalized insights, customized CVs, and smart recommendations powered by AI.
              </p>
            </div>
          </motion.div>
        </div>
        <motion.div
          className="flex w-full flex-col gap-6 lg:flex-row lg:items-center lg:justify-center"
          initial={{ y: 40, scale: 1.1, opacity: 0 }}
          whileInView={{ y: 0, scale: 1, opacity: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.48, ease: 'easeOut', type: 'tween' }}>
          {/* Left video */}
          <div className="flex w-full justify-center lg:w-auto lg:justify-center flex-shrink-0">
            <VideoWrapper className="w-[576px]" />
          </div>

          {/* Right cards container */}
          <div className="flex flex-col gap-4 items-center lg:items-start">
            <HomeCard variant="wide">
              {/* Header */}
              <div className="flex items-center gap-2 p-0">
                <FileTextRoundIcon className="w-10 h-10" />

                <span className="font-sans font-semibold text-[18px] leading-[28px] text-[#191F33]">
                  Resume Improvements
                </span>
              </div>

              {/* Items */}
              <div className="flex flex-col items-center lg:items-start gap-3 w-full">
                {improvementItems.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-start gap-2.5 p-4 w-full ${item.bgColor} rounded-xl border border-solid ${item.borderColor}`}>
                    <img className="w-5 h-5" alt="Check icon" src={item.icon} />
                    <div className="inline-flex flex-col items-start gap-0.5">
                      <div className="font-[number:var(--text-md-medium-font-weight)] text-gray-800 text-[length:var(--text-md-medium-font-size)] leading-[var(--text-md-medium-line-height)] whitespace-nowrap font-text-md-medium tracking-[var(--text-md-medium-letter-spacing)] [font-style:var(--text-md-medium-font-style)]">
                        {item.title}
                      </div>
                      <div className="font-[number:var(--text-md-regular-font-weight)] text-[#535862] text-[length:var(--text-md-regular-font-size)] leading-[var(--text-md-regular-line-height)] whitespace-nowrap font-text-md-regular tracking-[var(--text-md-regular-letter-spacing)] [font-style:var(--text-md-regular-font-style)]">
                        {item.description}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </HomeCard>

            <HomeCard variant="spacious">
              <div className="flex items-center gap-2 p-0">
                <TrendingRoundIcon className={'w-10 h-10'} />

                <span className="font-sans font-semibold text-[18px] leading-[28px] text-[#191F33]">
                  Skill Development
                </span>
              </div>
              {skillItems.map((skill, index) => (
                <div key={index} className="flex flex-col gap-2 w-full">
                  <div className="text-sm font-medium text-gray-800">{skill.name}</div>

                  <div className="relative w-full h-2 bg-gray-100 rounded-lg overflow-visible">
                    {/* Purple progress bar */}
                    <div
                      className="absolute left-0 top-0 h-full bg-[#7e56d8] rounded-lg"
                      style={{ width: `${skill.percentage}%` }}
                    />

                    {/* Tooltip */}
                    <div
                      className="absolute -top-10 z-10"
                      style={{ left: `calc(${skill.percentage}% - 22px)` }}>
                      <div className="flex flex-col items-center">
                        <div className="px-3 py-1 bg-white rounded-md text-xs font-semibold text-gray-500 shadow-md whitespace-nowrap">
                          {skill.percentage}%
                        </div>
                        <svg width="12" height="6" viewBox="0 0 12 6" fill="none">
                          <path d="M0 0L6 6L12 0" fill="white" />
                        </svg>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </HomeCard>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
